import * as yup from 'yup';
import { MiscAssetDTO } from '../../tables/MiscAssets/MiscAssets.meta';
import { EarmarkDTO } from '../../OtherAssets.meta';
import {
  EXCLUDE_OPT,
  getCustomBucketIndex,
} from '../components/AssetBucketSelect/AssetBucketSelect.meta';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';

export interface MiscAssetsFormValues {
  name: string;
  owner: string | null;
  marketValue: number | null;
  saleYear: string | null;
  saleNetEarnings: number | null;
  assetEarmarks: { value: string; label: string }[];
  assetGrowth: number | null;
  customBucketIndex: number | null;
  isLiquid: boolean;
  includeInGrossEstate: boolean;
  orionManagementStyleId: number | null;
}

export interface MiscAssetsFormDTO {
  Name: string;
  Owner: string;
  MarketValue: number | null;
  SaleYear: string | null;
  IsSoldAtRetirement: boolean;
  SaleNetEarnings: number | null;
  AssetEarmark: EarmarkDTO[];
  AssetGrowth: number | null;
  CustomBucketIndex: number | null;
  IsExcludedFromAssetBucketing: boolean;
  IsLiquid: boolean;
  IncludeInGrossEstate: boolean;
  OrionManagementStyleId: number | null;
}

export interface UpsertMiscAssetsDTO extends MiscAssetsFormDTO {
  clientId: string;
  Id: string | null;
}

interface DataToForm {
  data: MiscAssetDTO | null;
  options: OtherAssetsDTO['Options'];
}

export const dataToForm = ({ data, options }: DataToForm): MiscAssetsFormValues =>
  data
    ? {
        name: data.Name,
        owner: data.Owner,
        marketValue: data.MarketValue,
        saleYear:
          data.SaleYear !== null
            ? data.SaleYear.toString()
            : data.IsSoldAtRetirement
            ? 'At Retirement'
            : null,
        saleNetEarnings: data.SaleNetEarnings,
        assetEarmarks: data.AssetEarmark.map(({ Type, Tooltip }) => ({
          value: Type,
          label: Tooltip,
        })),
        assetGrowth: data.AssetGrowth,
        customBucketIndex: getCustomBucketIndex({
          orionManagementStyleId: data.OrionManagementStyleId,
          customBucketIndex: data.CustomBucketIndex,
          managementStyles: options.ManagementStyles,
          isExcludedFromAssetBucketing: data.IsExcludedFromAssetBucketing,
          assetBucketingByManagementStyle: options.AssetBucketingByManagementStyle,
        }),
        isLiquid: data.IsLiquid,
        includeInGrossEstate: data.IncludeInGrossEstate,
        orionManagementStyleId: data.OrionManagementStyleId,
      }
    : {
        name: '',
        owner: '',
        marketValue: null,
        saleYear: null,
        saleNetEarnings: null,
        assetEarmarks: [],
        assetGrowth: null,
        customBucketIndex: null,
        isLiquid: options.MiscellaneousAssetOptions.IsLiquid,
        includeInGrossEstate: true,
        orionManagementStyleId: null,
      };

export const formToBody = (values: MiscAssetsFormValues): MiscAssetsFormDTO => ({
  Name: values.name,
  Owner: values.owner || '',
  MarketValue: values.marketValue,
  SaleYear: values.saleYear === 'At Retirement' ? null : values.saleYear,
  IsSoldAtRetirement: values.saleYear === 'At Retirement',
  SaleNetEarnings: values.saleNetEarnings,
  AssetEarmark: values.assetEarmarks.map(({ value, label }) => ({ Type: value, Tooltip: label })),
  AssetGrowth: values.assetGrowth ?? 0,
  CustomBucketIndex:
    values.customBucketIndex === EXCLUDE_OPT.value ? null : values.customBucketIndex,
  IsExcludedFromAssetBucketing: values.customBucketIndex === EXCLUDE_OPT.value,
  IsLiquid: values.isLiquid,
  IncludeInGrossEstate: values.includeInGrossEstate,
  OrionManagementStyleId: values.orionManagementStyleId,
});

export const FORM_SCHEMA = yup.object().shape({
  name: yup.string().required('Please provide a name for this asset'),
  owner: yup.string().required('Please specify an owner'),
  marketValue: yup
    .number()
    .nullable()
    .notOneOf([0], 'Please specify a valid market value')
    .required('Please provide a market value'),
});
